/**
 * Type helpers to improve TypeScript inference for tRPC and reduce linting errors.
 * These provide better type inference than the complex generic types.
 */

import type { IntersectionError } from '@trpc/server/unstable-core-do-not-import';

type TrpcCollisionMessage = IntersectionError<string>;
const TRPC_COLLISION_PREFIX = "The property '";

type StripTrpcCollision<T> = T extends TrpcCollisionMessage ? never : T;
type StripTrpcError<T> = T extends Error ? never : T;

export type TrpcSegment<T> = StripTrpcError<StripTrpcCollision<T>>;

// Simple wrapper + assertion to help TypeScript infer tRPC router segments
export function assertTrpcSegment<T>(segment: unknown): asserts segment is TrpcSegment<T> {
  if (segment instanceof Error) {
    throw segment;
  }
  if (typeof segment === 'string' && segment.startsWith(TRPC_COLLISION_PREFIX)) {
    throw new Error(segment);
  }
}

const buildUnavailableSegmentProxy = <T>(label?: string): TrpcSegment<T> => {
  const describe = label ? ` "${label}"` : '';
  const throwUnavailable = (): never => {
    throw new Error(`tRPC segment${describe} is unavailable in this runtime.`);
  };

  const createProxy = (): TrpcSegment<T> => {
    const proxy = new Proxy(
      () => {
        throwUnavailable();
      },
      {
        get(_target, property) {
          if (property === 'then' || property === 'toJSON' || property === Symbol.toStringTag) {
            return;
          }
          return createProxy();
        },
        apply() {
          throwUnavailable();
        },
      }
    );
    assertTrpcSegment<T>(proxy);
    return proxy;
  };

  return createProxy();
};

export function createTrpcSegment<T>(segment: T | null | undefined, options?: { label?: string }): TrpcSegment<T> {
  if (segment === undefined || segment === null) {
    return buildUnavailableSegmentProxy<T>(options?.label);
  }
  assertTrpcSegment<T>(segment);
  return segment;
}

// Helper for React Query mutation types to avoid inference issues
export interface TypedMutationResult<TData = unknown, TError = Error, TVariables = void> {
  data: TData | undefined;
  error: TError | null;
  failureCount: number;
  failureReason: TError | null;
  isError: boolean;
  isFetched: boolean;
  isFetchedAfterMount: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isPaused: boolean;
  isPending: boolean;
  isSuccess: boolean;
  mutate: (
    variables: TVariables,
    options?: { onSuccess?: (data: TData) => void; onError?: (error: TError) => void }
  ) => void;
  mutateAsync: (variables: TVariables) => Promise<TData>;
  reset: () => void;
  status: 'pending' | 'error' | 'success';
}

// Helper for React Query query types
export interface TypedQueryResult<TData = unknown, TError = Error> {
  data: TData | undefined;
  error: TError | null;
  isError: boolean;
  isFetched: boolean;
  isFetchedAfterMount: boolean;
  isFetching: boolean;
  isInitialLoading: boolean;
  isLoading: boolean;
  isLoadingError: boolean;
  isPlaceholderData: boolean;
  isRefetchError: boolean;
  isRefetching: boolean;
  isSuccess: boolean;
  refetch: () => Promise<TypedQueryResult<TData, TError>>;
  status: 'pending' | 'error' | 'success';
}

// Helper to provide explicit typing for database query results
export type DbQueryResult<T = Record<string, unknown>> = T[];

// Helper for API response types
export type ApiResponse<T = unknown> = {
  data?: T;
  error?: string | null;
  success?: boolean;
  meta?: Record<string, unknown>;
};
